// Book comparison data - All information is verified from GitHub repos and official sources
// NO HALLUCINATION - every claim has a source

export interface BookData {
  id: string;
  title: string;
  shortTitle: string;
  author: string;
  year: number;
  coverImage: string;
  description: string;
  amazonUrl?: string; // Primary purchase link (affiliate)
  githubUrl: string;
  officialUrl?: string;
  // Quantitative metrics (verified from GitHub)
  metrics: {
    pythonFiles: number;
    notebookFiles: number;
    totalCodeFiles: number;
    source: string;
  };
  // Qualitative dimensions (scored 1-5, with justification)
  dimensions: {
    slug: string;
    name: string;
    score: number;
    justification: string;
    source: string;
  }[];
  // What the book covers
  topics: string[];
  // Who should read this
  audience: string[];
  // What this book focuses on (factual, from TOC/README)
  focus: string[];
  // Is this the author's book?
  isAuthorBook?: boolean;
}

export interface ComparisonDimension {
  slug: string;
  name: string;
  description: string;
  howEvaluated: string;
}

// 4 consolidated dimensions with clear evaluation criteria
export const comparisonDimensions: ComparisonDimension[] = [
  {
    slug: "agent-concepts",
    name: "Agent Concepts",
    description:
      "Coverage of agent-specific concepts: tools, structured output, orchestration, observability, memory, planning, reasoning",
    howEvaluated:
      "Review TOC and chapters for agent-specific topics vs general GenAI/LLM fundamentals",
  },
  {
    slug: "multi-agent-patterns",
    name: "Multi-Agent Patterns",
    description:
      "Coverage of multi-agent coordination, communication, team structures, handoffs, and orchestration patterns",
    howEvaluated:
      "Check for patterns like supervisor, swarm, handoffs, conversation-driven orchestration, workflow patterns",
  },
  {
    slug: "hands-on-implementation",
    name: "Hands-on Implementation",
    description:
      "Emphasis on practical implementation with runnable code examples and complete applications",
    howEvaluated:
      "Count Python + Notebook files, review for end-to-end applications vs isolated snippets",
  },
  {
    slug: "ux-principles",
    name: "UX Principles",
    description:
      "Coverage of user experience for AI applications: streaming, feedback, human-in-the-loop, error handling, interfaces",
    howEvaluated: "Review for UX-focused chapters or sections in TOC",
  },
];

// Book data - VERIFIED FROM GITHUB AND OFFICIAL SOURCES
export const books: BookData[] = [
  {
    id: "designing-multiagent-systems",
    title: "Designing Multi-Agent Systems",
    shortTitle: "Designing MAS",
    author: "Victor Dibia",
    year: 2025,
    coverImage: "/images/bookcover.webp",
    description:
      "15 chapters across 4 parts teaching multi-agent systems from first principles. Part I covers theory (patterns, UX principles). Part II guides the reader through building a full featured but hackable multi-agent framework called - PicoAgents - from scratch. Spefically, you build an agent class (add model clients, tool calling, middleware, observability with opentelemetry, human input and approvals, agents as tool), extend it to build a computer use agent, builld multi-agent workflows, orchestration, and learn to integrate these into a web application. Part III covers evaluation, optimization, protocols (MCP/A2A), and ethics. Part IV provides full case studies (you build a multiagent workflow for structured data analysis and a software engineering agent). The GitHub repo also contains examples of the same concepts implemented using the Microsoft Agent Framework (successor to AutoGen and Semantic Kernel), Goole ADK, and LangGraph.\n",
    amazonUrl: "https://www.amazon.com/dp/B0G2BCQQJY",
    githubUrl: "https://github.com/victordibia/designing-multiagent-systems",
    officialUrl: "https://multiagentbook.com",
    metrics: {
      pythonFiles: 262,
      notebookFiles: 14,
      totalCodeFiles: 276,
      source: "GitHub repo analysis (Nov 2025)",
    },
    dimensions: [
      {
        slug: "agent-concepts",
        name: "Agent Concepts",
        score: 5,
        justification:
          "15 chapters covering agent execution loop, tools, memory, middleware, computer use agents, evaluation trajectories, optimization strategies, and distributed protocols (MCP, A2A).",
        source: "TOC: 4-part structure with 15 chapters all agent-focused",
      },
      {
        slug: "multi-agent-patterns",
        name: "Multi-Agent Patterns",
        score: 5,
        justification:
          "Ch2 provides full taxonomy: deterministic (sequential, conditional, parallel, supervisor) and autonomous (plan-based, handoff, conversation-driven). Ch6-7 implement each pattern from scratch.",
        source: "TOC: Ch2 taxonomy, Ch6-7 implementation",
      },
      {
        slug: "hands-on-implementation",
        name: "Hands-on Implementation",
        score: 5,
        justification:
          "Builds complete PicoAgents library from scratch across Part II (6 chapters). Includes evaluation framework, optimization techniques, and two full case studies (business Q&A, software engineering agent).",
        source: "GitHub: 262 .py + 14 .ipynb, Part II + Part IV",
      },
      {
        slug: "ux-principles",
        name: "UX Principles",
        score: 5,
        justification:
          "Ch3 establishes 4 UX principles (capability discovery, cost-aware delegation, observability, interruptibility). Ch8 provides a guide on building modern agent UIs with FastAPI + SSE backend, Vanilla JS and React frontend with streaming.",
        source: "TOC: Ch3 UX Principles, Ch8 Web Implementation",
      },
    ],
    topics: [
      "Agent execution loop and middleware",
      "Multi-agent orchestration patterns (6 patterns)",
      "Computer use agents (DOM, visual, hybrid)",
      "Deterministic workflows with checkpointing",
      "Autonomous orchestration and termination",
      "Agent UX and web applications (SSE, React)",
      "Evaluation with trajectories and LLM judges",
      "10 failure modes and optimization strategies",
      "MCP and A2A protocols for distributed agents",
      "Ethics, security, and responsible AI",
      "Case studies: Business Q&A, Software Engineering Agent",
    ],
    audience: [
      "System architects and software engineers",
      "Technical leads making architecture decisions",
      "AI engineers transitioning to agents",
      "Product managers understanding capabilities",
    ],
    focus: [
      "Part I: Theory foundation—agent definition, 6 orchestration patterns (sequential, conditional, parallel, supervisor, handoff, conversation-driven), 4 UX principles",
      "Part II: Build PicoAgents library from scratch—Ch4 covers BaseAgent (async loop, streaming), BaseChatCompletionClient (multi-provider LLMs), structured output (Pydantic), BaseTool/FunctionTool (auto-schema from type hints), CancellationToken, BaseMemory (RAG), MemoryTool (cross-session learning), BaseMiddleware (security/PII/rate-limiting/OpenTelemetry), context engineering (compaction, isolation, filtering), human-in-the-loop (tool approval, stateless clarification), agents-as-tools; Ch5 covers computer use agents; Ch6-7 cover workflows and autonomous orchestration; Ch8 covers web UX with FastAPI/SSE/React",
      "Part III: Production readiness—trajectory evaluation, 10 failure modes, MCP/A2A protocols, ethics and security",
      "Part IV: Complete case studies—business Q&A from unstructured data, software engineering agent",
      "276 code files, framework-agnostic patterns, digital version with quarterly updates",
    ],
    isAuthorBook: true,
  },
  {
    id: "generative-ai-design-patterns",
    title: "Generative AI Design Patterns",
    shortTitle: "GenAI Patterns",
    author: "Valliappa Lakshmanan & Hannes Hapke",
    year: 2025,
    coverImage: "/images/books/genaipatterns.jpg",
    description:
      "Covers 32 design patterns for generative AI including content control, RAG, reasoning, reliability, and agents. Uses a structured problem/solution format for each pattern.",
    amazonUrl: "https://amzn.to/4ixAhvW",
    githubUrl: "https://github.com/lakshmanok/generative-ai-design-patterns",
    metrics: {
      pythonFiles: 64,
      notebookFiles: 50,
      totalCodeFiles: 114,
      source: "GitHub repo analysis (Nov 2025)",
    },
    dimensions: [
      {
        slug: "agent-concepts",
        name: "Agent Concepts",
        score: 3,
        justification:
          "Chapter 7 covers Tool Calling, Code Execution, and Multi-agent patterns. Broader focus on 32 GenAI patterns including RAG, reasoning, and reliability.",
        source: "README: 32 patterns, 3 in Ch7 are agent-specific",
      },
      {
        slug: "multi-agent-patterns",
        name: "Multi-Agent Patterns",
        score: 3,
        justification:
          "Pattern #23 covers multi-agent collaboration. Additional supporting patterns include LLM-as-Judge (#17), Reflection (#18), and Long-Term Memory (#28) that enable multi-agent systems.",
        source: "TOC: Patterns 17, 18, 23, 28",
      },
      {
        slug: "hands-on-implementation",
        name: "Hands-on Implementation",
        score: 4,
        justification:
          "Dedicated example folder for each of 32 patterns. Pattern-focused examples rather than complete applications.",
        source: "GitHub: 64 .py + 50 .ipynb files",
      },
      {
        slug: "ux-principles",
        name: "UX Principles",
        score: 1,
        justification:
          "Focus is on backend patterns: content control, retrieval, reasoning, and reliability. Valuable foundation for agent backends.",
        source: "README TOC review",
      },
    ],
    topics: [
      "Content style control",
      "RAG patterns",
      "Reasoning (CoT, ToT)",
      "Reliability patterns",
      "Tool calling",
      "Constraints and optimization",
      "Guardrails",
    ],
    audience: [
      "ML engineers building GenAI applications",
      "Architects designing GenAI systems",
      "Developers familiar with design patterns",
    ],
    focus: [
      "Broad coverage of 32 GenAI patterns",
      "Structured problem/solution format",
      "Strong RAG and reliability coverage",
      "Companion to ML Design Patterns book",
    ],
  },
  {
    id: "building-applications-ai-agents",
    title: "Building Applications with AI Agents",
    shortTitle: "Building AI Agents",
    author: "Michael Albada",
    year: 2025,
    coverImage: "/images/books/aiagentsablada.jpeg",
    description:
      "A solid introductory resource for folks new to AI agents. Notably covers human-agent collaboration, trust, and governance (Ch10), plus learning from experience with fine-tuning approaches (Ch6). Includes code samples across 3 frameworks (LangGraph, LangChain, AutoGen)—though these may become outdated given rapid framework evolution.",
    amazonUrl: "https://amzn.to/48LALtV",
    githubUrl:
      "https://github.com/michaelalbada/BuildingApplicationsWithAIAgents",
    metrics: {
      pythonFiles: 62,
      notebookFiles: 0,
      totalCodeFiles: 62,
      source: "GitHub repo analysis (Nov 2025)",
    },
    dimensions: [
      {
        slug: "agent-concepts",
        name: "Agent Concepts",
        score: 4,
        justification:
          "Covers scenario specs, multi-framework implementations, evaluation harness, observability.",
        source: "README: multi-framework platform for AI agents",
      },
      {
        slug: "multi-agent-patterns",
        name: "Multi-Agent Patterns",
        score: 3,
        justification:
          "Ch7 covers multiagent coordination patterns (democratic, hierarchical, actor-critic), communication protocols, and conflict resolution. Implements across multiple frameworks.",
        source: "TOC: Ch7 Multiagent Coordination",
      },
      {
        slug: "hands-on-implementation",
        name: "Hands-on Implementation",
        score: 4,
        justification:
          "Multiple scenario-based applications: ecommerce, financial services, healthcare, IT helpdesk, legal, SOC, supply chain.",
        source: "GitHub: src/frameworks/langgraph_agents/ folders",
      },
      {
        slug: "ux-principles",
        name: "UX Principles",
        score: 3,
        justification:
          "Ch10 covers human-agent collaboration in depth: roles/autonomy, trust lifecycle, escalation design, oversight, shared memory, governance, and compliance. Production-focused but addresses user-facing concerns.",
        source: "TOC: Ch10 Human-Agent Collaboration",
      },
    ],
    topics: [
      "Multi-framework development",
      "LangGraph implementation",
      "LangChain implementation",
      "AutoGen implementation",
      "Observability (Loki, OpenTelemetry)",
      "Evaluation harness",
      "Industry scenarios",
    ],
    audience: [
      "Developers comparing agent frameworks",
      "Teams building production agents",
      "Engineers setting up observability",
    ],
    focus: [
      "Same scenarios implemented across frameworks",
      "Built-in observability setup",
      "Industry-specific examples",
      "Framework-agnostic scenario specs",
    ],
  },
  {
    id: "reasoning-from-scratch",
    title: "Build a Reasoning Model (From Scratch)",
    shortTitle: "Reasoning from Scratch",
    author: "Sebastian Raschka",
    year: 2026,
    coverImage: "/images/books/reasoningscratch.jpg",
    description:
      "Starts from a pre-trained base model and adds reasoning to it in code: evaluation and answer grading first, then inference-time scaling (temperature and top-p sampling, self-consistency voting), then self-refinement, then training with reinforcement learning from verifiable rewards and GRPO, then distillation. Mirrors how models like DeepSeek R1 are actually built, at a size that runs locally. Eight chapters and seven appendixes; the appendixes cover the Qwen3 source, larger models, batched throughput and a chat interface.",
    amazonUrl: "https://amzn.to/4zDo54V",
    githubUrl: "https://github.com/rasbt/reasoning-from-scratch",
    officialUrl: "https://www.manning.com/books/build-a-reasoning-model-from-scratch",
    metrics: {
      pythonFiles: 93,
      notebookFiles: 19,
      totalCodeFiles: 112,
      source: "GitHub repo analysis (Aug 2026)",
    },
    dimensions: [
      {
        slug: "agent-concepts",
        name: "Agent Concepts",
        score: 2,
        justification:
          "Reasoning is covered in depth — inference-time scaling, self-consistency, self-refinement — and Ch3 builds the answer grading that verifiable rewards depend on. Tools, memory and orchestration are out of scope.",
        source: "TOC: Ch3-Ch5 reasoning and evaluation; no tool or memory chapters",
      },
      {
        slug: "multi-agent-patterns",
        name: "Multi-Agent Patterns",
        score: 1,
        justification:
          "Single model throughout. No coordination, handoff or orchestration content.",
        source: "TOC review: 8 chapters, none multi-agent",
      },
      {
        slug: "hands-on-implementation",
        name: "Hands-on Implementation",
        score: 5,
        justification:
          "A working GRPO training loop in reasoning_from_scratch/ch06.py (reward_rlvr, sequence_logprob, compute_grpo_loss, train_rlvr_grpo), with batched and FSDP variants, then Ch7 adds KL, clip ratio and format rewards as separate runnable scripts. CI runs the code on Linux, macOS and Windows.",
        source: "GitHub: 93 .py + 19 .ipynb, per-chapter modules and test workflows",
      },
      {
        slug: "ux-principles",
        name: "UX Principles",
        score: 1,
        justification:
          "One appendix builds a chat interface. Otherwise the subject is model internals.",
        source: "TOC: Appendix on chat interfaces only",
      },
    ],
    topics: [
      "Inference-time scaling (temperature, top-p, self-consistency)",
      "Self-refinement",
      "Reinforcement learning from verifiable rewards",
      "GRPO and its variants",
      "Distillation",
      "Reasoning evaluation and answer grading",
    ],
    audience: [
      "Engineers who want to know how reasoning models are trained",
      "Readers of Build a Large Language Model (From Scratch)",
      "Developers with PyTorch experience",
    ],
    focus: [
      "Adds reasoning to a pre-trained base model, step by step",
      "Mirrors DeepSeek R1-style training at a runnable size",
      "Reinforcement learning implemented rather than described",
      "Printed in color; 440 pages",
    ],
  },
  {
    id: "llms-from-scratch",
    title: "Build a Large Language Model (From Scratch)",
    shortTitle: "LLMs from Scratch",
    author: "Sebastian Raschka",
    year: 2024,
    coverImage: "/images/books/llmscratch.webp",
    description:
      "Teaches how LLMs work by implementing them from scratch. Covers attention mechanisms, pretraining, and finetuning. Builds a GPT-like model step by step.",
    amazonUrl: "https://amzn.to/4pDf5qz",
    githubUrl: "https://github.com/rasbt/LLMs-from-scratch",
    metrics: {
      pythonFiles: 122,
      notebookFiles: 55,
      totalCodeFiles: 177,
      source: "GitHub repo analysis (Nov 2025)",
    },
    dimensions: [
      {
        slug: "agent-concepts",
        name: "Agent Concepts",
        score: 1,
        justification:
          "Focuses on understanding LLMs at the architecture level - essential foundation for understanding what agents call under the hood.",
        source: "README TOC: Ch1-7 are model building",
      },
      {
        slug: "multi-agent-patterns",
        name: "Multi-Agent Patterns",
        score: 1,
        justification:
          "Focuses on single model training and finetuning. Valuable for teams building custom models for agent systems.",
        source: "README: no multi-agent topics in TOC",
      },
      {
        slug: "hands-on-implementation",
        name: "Hands-on Implementation",
        score: 5,
        justification:
          "Extensive notebooks building GPT from scratch. Every concept implemented in code.",
        source: "GitHub: 122 .py + 55 .ipynb files",
      },
      {
        slug: "ux-principles",
        name: "UX Principles",
        score: 1,
        justification:
          "Focus is on model internals: attention, pretraining, finetuning. Deep understanding helps debug agent behavior.",
        source: "README TOC review",
      },
    ],
    topics: [
      "LLM architecture",
      "Tokenization",
      "Attention mechanisms",
      "GPT implementation",
      "Pretraining",
      "Finetuning",
      "LoRA",
    ],
    audience: [
      "ML engineers learning LLM internals",
      "Researchers building custom models",
      "Developers with PyTorch experience",
    ],
    focus: [
      "Deep understanding of LLM internals",
      "From-scratch implementation approach",
      "Includes video course companion",
      "Bonus implementations: Llama 3.2, Qwen3, Gemma 3",
    ],
  },
];
